<template>
	<v-dialog
		v-model="localShowDialog"
		@click:outside="closeDialog"
		@keydown.esc="closeDialog"
		@keydown.enter="initiateUpgrade"
		:width="$vuetify.display.mobile ? '95%' : '650px'"
	>
		<v-card data-test="upgrade-hint-dialog">
			<div class="dialog-header">
				<span class="sgnl-subtitle-medium">{{ title }}</span>
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="closeDialog"
				>
					<Close16 />
				</v-btn>
			</div>
			<v-divider />
			<v-card-text class="dialog-body">
				<div class="upgrade-content">
					<div class="tier-upgrade-details-stack">
						<div class="sgnl-paragraph-m-regular sgnl-text-color-secondary">
							{{ description }}
							{{ upgradeBenefitsText }}
						</div>
						<div class="sgnl-paragraph-m-regular sgnl-text-color-secondary">
							{{ nextTierIntroText }}
						</div>
						<TierHighlightsList :tierHighlightsToDisplay="tierHighlightsToDisplay" />
					</div>
					<img
						class="side-image"
						:src="sideImageURL"
					/>
				</div>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<a
					v-if="quickCheckout"
					class="sgnl-caption-regular upgrade-compare-link"
					@click="goToBillingPage"
					>Compare Account Tier Features.</a
				>
				<v-spacer />
				<v-btn
					variant="flat"
					color="primary"
					@click="initiateUpgrade"
				>
					<span>{{ upgradeButtonStyling.text }}</span>
					<Upgrade16 />
				</v-btn>
			</v-card-actions>
		</v-card>
		<v-dialog
			v-model="showContactSalesFormDialog"
			class="sgnl-text-center"
			:width="$vuetify.display.mobile ? '100%' : '50%'"
		>
			<v-card class="sgnl-p-16 sgnl-p-lg-40">
				<ContactSalesForm
					:passedPlan="contactSalesFormProps.plan"
					:passedEmail="contactSalesFormProps.email"
					:passedFullname="contactSalesFormProps.fullname"
					@form-submitted="handleContactSalesFormSubmitted"
					@form-submission-failed="handleContactSalesFormSubmissionFailed"
				/>
			</v-card>
		</v-dialog>

		<v-dialog
			v-model="showManageSubscriptionFormDialog"
			class="sgnl-text-center"
			:width="$vuetify.display.mobile ? '100%' : '50%'"
		>
			<v-card class="sgnl-p-16 sgnl-p-lg-40">
				<ManageSubscriptionForm
					:passedCurrentTier="manageSubscriptionFormProps.currentTier"
					:passedEmail="manageSubscriptionFormProps.email"
					:passedFullname="manageSubscriptionFormProps.fullname"
					@form-submitted="handleManageSubscriptionFormSubmitted"
					@form-submission-failed="handleManageSubscriptionFormSubmissionFailed"
				/>
			</v-card>
		</v-dialog>

		<v-snackbar
			v-model="successSnackbar"
			:timeout="4000"
			class="snackbar-success"
		>
			<div class="snackbar-icon-container snackbar-success">
				<CheckmarkFilled16 />
			</div>
			{{ successMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="successSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>

		<v-snackbar
			v-model="errorSnackbar"
			:timeout="6000"
			class="snackbar-error"
		>
			<div class="snackbar-icon-container snackbar-error">
				<ErrorFilled16 />
			</div>
			{{ errorMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="errorSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

// Carbon Icons
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import Close16 from "@carbon/icons-vue/es/close/16";
import Upgrade16 from "@carbon/icons-vue/es/upgrade/16";
import ErrorFilled16 from "@carbon/icons-vue/es/error--filled/16";

// Types
import {
	AccountGroupCode,
	UserLimits,
	UserLimitsE,
	ResolvedTierDetail,
	TierFeatureHighlight,
	tierFromCode,
} from "@/js/tierconfig";

// Libraries
// Utilities
import * as util from "@/js/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";

// Components
import TierHighlightsList from "@/components/Common/TierHighlightsList.vue";
import ContactSalesForm from "@/components/ContactSalesForm.vue";
import ManageSubscriptionForm from "@/components/ManageSubscriptionForm.vue";

// Stores
import { mapState } from "pinia";
import { useUserStore } from "@/stores/user";
import { postCheckoutSession, getStripeCheckoutPublishableKey } from "@/js/signaloidClient";

// Global variables

type DialogTypes = "info" | "warning";

type ComponentData = {
	stripePublishableKey: string;
	stripeSessionId: string;
	monthlySubscription: boolean;
	showContactSalesFormDialog: boolean;
	showManageSubscriptionFormDialog: boolean;
	contactSalesFormProps: {
		plan: string;
		fullname: string;
		email: string;
	};
	manageSubscriptionFormProps: {
		currentTier: string;
		fullname: string;
		email: string;
	};
	quickCheckout: boolean;
	successSnackbar: boolean;
	successMessage: string;
	errorSnackbar: boolean;
	errorMessage: string;
};

export default defineComponent({
	name: "TierUpgradeHintDialog",
	components: {
		TierHighlightsList,
		ContactSalesForm,
		ManageSubscriptionForm,
		CheckmarkFilled16,
		Close16,
		Upgrade16,
		ErrorFilled16,
	},
	emits: ["update:showDialog", "customer-portal"],
	props: {
		showDialog: { type: Boolean, default: false },
		messageType: { type: String as PropType<DialogTypes>, default: "info" },
		triggeredLimit: { type: String as PropType<UserLimits> | undefined, default: undefined },
		metadata: { type: Object },
	},
	data: (): ComponentData => ({
		stripePublishableKey: "",
		stripeSessionId: "",
		monthlySubscription: true,
		showContactSalesFormDialog: false,
		showManageSubscriptionFormDialog: false,
		contactSalesFormProps: {
			plan: "",
			fullname: "",
			email: "",
		},
		manageSubscriptionFormProps: {
			currentTier: "",
			fullname: "",
			email: "",
		},
		quickCheckout: false,
		successSnackbar: false,
		successMessage: "",
		errorSnackbar: false,
		errorMessage: "",
	}),
	setup() {
		const userStore = useUserStore();
		return { userStore };
	},
	computed: {
		...mapState(useUserStore, {
			currentUserResourceUsage: "currentUserResourceUsage",
			currentUserTierDetails: "currentUserTierDetails",
			currentUserTierCode: "currentUserTierCode",
		}),
		localShowDialog: {
			get(): boolean {
				return this.showDialog;
			},
			set(value: boolean) {
				this.$emit("update:showDialog", value);
			},
		},
		suggestedUserTierCode(): AccountGroupCode {
			switch (this.currentUserTierCode || this.currentUserTierDetails?.code) {
				case "free-tier":
					return "developer-tier";
				case "developer-tier":
					return "enterprise-tier-s";
				case "pro-tier":
					return "enterprise-tier-s";
				case "enterprise-tier-s":
					return "enterprise-tier-m";
				case "enterprise-tier-m":
					return "enterprise-tier-l";
				default:
					return "enterprise-tier-l";
			}
		},
		nextUserTierDetails(): ResolvedTierDetail | undefined {
			return this.userStore.tierDetails.find((e) => e.code === this.suggestedUserTierCode);
		},
		sideImageURL() {
			const imagePath = "images/overviewVideoThumbnail_abstract.png";
			return new URL(imagePath, import.meta.env.VITE_ASSET_SERVER_URL).toString();
		},
		currentTierName(): string {
			return this.currentUserTierDetails?.fullTitle ?? "Current Tier";
		},
		nextTierName(): string {
			// Map tier codes to display names as fallback
			const tierNameMap: Record<string, string> = {
				"developer-tier": "Developer Tier",
				"pro-tier": "Pro Tier",
				"enterprise-tier-s": "Enterprise S Tier",
				"enterprise-tier-m": "Enterprise M Tier",
				"enterprise-tier-l": "Enterprise L Tier",
			};

			return this.nextUserTierDetails?.fullTitle ?? tierNameMap[this.suggestedUserTierCode] ?? "Next Tier";
		},
		title(): string {
			if (this.hitEnterpriseTierLimit) {
				return `You've Outgrown your ${this.currentTierName} quota`;
			} else {
				return `You've Outgrown the ${this.currentTierName}`;
			}
		},
		description(): string {
			let currentAllowance;

			if (
				this.currentUserTierDetails?.allowance &&
				this.triggeredLimit !== undefined &&
				this.currentUserTierDetails.allowance[this.triggeredLimit] === undefined
			) {
				/*
				 * Handle unlimited tier limits
				 * This code path should never hit, if it has, something has gone wrong
				 */
				monitoringCaptureError(
					new Error("User exceeded a supposedly unlimited limit"),
					"Display tier upgrade dialog",
				);

				if (this.hitEnterpriseTierLimit) {
					return `You have used up your ${this.currentTierName} resource allocation. Please contact support at developer-support@signaloid.com.`;
				} else {
					return `You have hit a limit of the ${this.currentTierName}. Please contact support at developer-support@signaloid.com.`;
				}
			}

			switch (this.triggeredLimit) {
				case UserLimitsE.DynamicInstructionCount:
					return `You have reached your ${this.currentTierName} computation limit for this month.`;

				case UserLimitsE.ConcurrentTaskCount:
					currentAllowance = util.numberLessThanTenToWords(
						this.currentUserTierDetails?.allowance.ConcurrentTaskCount,
					);
					return `On the ${this.currentTierName} you can only run ${currentAllowance} task(s) concurrently.`;

				case UserLimitsE.CoreCount:
					currentAllowance = util.numberLessThanTenToWords(this.currentUserTierDetails?.allowance.CoreCount);
					return `You can only create ${currentAllowance} custom C0 processor core(s) on the ${this.currentTierName}.`;

				case UserLimitsE.RepositoryCount:
					currentAllowance = util.numberLessThanTenToWords(
						this.currentUserTierDetails?.allowance.RepositoryCount,
					);
					return `You can only connect ${currentAllowance} GitHub ${
						this.currentUserTierDetails?.allowance.ConcurrentTaskCount == 1 ? "repository" : "repositories"
					} to your account on the ${this.currentTierName}.`;

				case UserLimitsE.DataDriveCount:
					currentAllowance = util.numberLessThanTenToWords(
						this.currentUserTierDetails?.allowance.DataDriveCount,
					);
					return `You can only create ${currentAllowance} data drive(s) on the ${this.currentTierName}.`;

				case UserLimitsE.PlotCount:
					currentAllowance = util.numberLessThanTenToWords(this.currentUserTierDetails?.allowance.PlotCount);
					return `You can only generate ${currentAllowance} distribution plots per month on the ${this.currentTierName}.`;

				case UserLimitsE.BucketCount:
					currentAllowance = util.numberLessThanTenToWords(
						this.currentUserTierDetails?.allowance.BucketCount,
					);

					if (currentAllowance && currentAllowance > 0) {
						return `You can only connect to ${currentAllowance} AWS S3 Buckets on the ${this.currentTierName}.`;
					} else {
						return `You cannot connect to AWS S3 Buckets on the ${this.currentTierName}.`;
					}

				case UserLimitsE.CloudStorageBytes:
					currentAllowance = util.toStringWithByteMagnitude(
						this.currentUserTierDetails?.allowance.CloudStorageBytes,
					);
					return `On the ${this.currentTierName} your cloud storage allowance is limited to ${currentAllowance}.`;

				case UserLimitsE.AllowedCoreClasses:
					currentAllowance = this.currentUserTierDetails?.allowance.AllowedCoreClasses;

					if (currentAllowance?.length) {
						return `On the ${this.currentTierName} you can only access ${
							currentAllowance.length === 1 ? "the " : ""
						}${this.listToReadableString(currentAllowance)} core ${
							currentAllowance.length === 1 ? "class" : "classes"
						}.`;
					}
					break;

				case UserLimitsE.AllowedMicroArchitectures:
					currentAllowance = this.currentUserTierDetails?.allowance.AllowedMicroArchitectures;

					if (currentAllowance?.length) {
						return `On the ${this.currentTierName} you can only access ${util.numberLessThanTenToWords(
							currentAllowance.length,
						)} Signaloid C0 processor ${
							currentAllowance.length === 1 ? "microarchitecture" : "microarchitectures"
						}.`;
					}
					break;

				case UserLimitsE.KeyCount:
					currentAllowance = util.numberLessThanTenToWords(this.currentUserTierDetails?.allowance.KeyCount);
					return `On the ${this.currentTierName} you can only have up to ${currentAllowance} API keys.`;
				default:
					break;
			}

			if (this.hitEnterpriseTierLimit) {
				return `You have used up your ${this.currentTierName} resource allocation.`;
			} else {
				return `You have hit a limit of the ${this.currentTierName}.`;
			}
		},
		upgradeBenefitsText(): string {
			// If enterprise tier dont add an upgrade benefits intro text
			if (this.hitEnterpriseTierLimit) {
				return "";
			}

			let upgradedAllowance;
			let currentAllowance;
			switch (this.triggeredLimit) {
				case UserLimitsE.DynamicInstructionCount:
					return `Upgrade your account to increase your monthly quota.`;

				case UserLimitsE.ConcurrentTaskCount:
					return `Upgrade your account to run more tasks in parallel.`;

				case UserLimitsE.CoreCount:
					upgradedAllowance = util.numberLessThanTenToWords(this.nextUserTierDetails?.allowance.CoreCount);
					return `Upgrade your account to the ${this.nextTierName} to create up to ${upgradedAllowance} custom cores.`;

				case UserLimitsE.RepositoryCount:
					upgradedAllowance = util.numberLessThanTenToWords(
						this.nextUserTierDetails?.allowance.RepositoryCount,
					);
					return `Upgrade your account to the ${this.nextTierName} to connect up to ${upgradedAllowance} repositories.`;

				case UserLimitsE.DataDriveCount:
					upgradedAllowance = util.numberLessThanTenToWords(
						this.nextUserTierDetails?.allowance.DataDriveCount,
					);
					return `Upgrade your account to the ${this.nextTierName} to connect up to ${upgradedAllowance} data drive(s).`;

				case UserLimitsE.PlotCount:
					upgradedAllowance = util.numberLessThanTenToWords(this.nextUserTierDetails?.allowance.PlotCount);
					return `Upgrade your account to the ${this.nextTierName} to generate up to ${upgradedAllowance} plots per month.`;

				case UserLimitsE.CloudStorageBytes:
					upgradedAllowance = util.toStringWithByteMagnitude(
						this.nextUserTierDetails?.allowance.CloudStorageBytes,
					);
					return `Upgrade your account to the ${this.nextTierName} to access up to ${upgradedAllowance} in cloud storage.`;

				case UserLimitsE.AllowedCoreClasses:
					upgradedAllowance = this.nextUserTierDetails?.allowance.AllowedCoreClasses ?? [];
					currentAllowance = this.currentUserTierDetails?.allowance.AllowedCoreClasses ?? [];

					upgradedAllowance = upgradedAllowance.filter((v) => !currentAllowance.includes(v));

					if (upgradedAllowance.length > 0) {
						return `Upgrade your account to the ${this.nextTierName} to access ${
							upgradedAllowance.length === 1 ? "the " : ""
						}${this.listToReadableString(upgradedAllowance)} core ${
							upgradedAllowance.length === 1 ? "class" : "classes"
						}.`;
					}
					return `Upgrade your account to the ${this.nextTierName} to access more core classes.`;

				case UserLimitsE.AllowedMicroArchitectures:
					upgradedAllowance = this.nextUserTierDetails?.allowance.AllowedMicroArchitectures ?? [];
					currentAllowance = this.currentUserTierDetails?.allowance.AllowedMicroArchitectures;

					upgradedAllowance = currentAllowance?.length
						? upgradedAllowance.filter((v) => !currentAllowance.includes(v))
						: [];

					if (upgradedAllowance.length > 0) {
						return `Upgrade your account to the ${this.nextTierName} to access
						${upgradedAllowance.length === 1 ? "the" : ""}
						${this.listToReadableString(upgradedAllowance)}
						core ${upgradedAllowance.length === 1 ? "microarchitecture" : "microarchitectures"}.`;
					} else {
						return `Upgrade your account to the ${this.nextTierName} to access more core microarchitectures.`;
					}
				case UserLimitsE.KeyCount:
					upgradedAllowance = util.numberLessThanTenToWords(this.nextUserTierDetails?.allowance.KeyCount);
					return `Upgrade your account to the ${this.nextTierName} to access up to create up to ${upgradedAllowance} API keys.`;

				default:
					return `Upgrade your account to the ${this.nextTierName} to access more features.`;
			}
		},
		nextTierIntroText(): string {
			const defaultNextTierIntro = this.hitEnterpriseTierLimit
				? `\nContact us to increase your quota for:`
				: `\nUpgrade to the ${this.nextTierName} for:`;
			switch (this.triggeredLimit) {
				default:
					return defaultNextTierIntro;
			}
		},
		tierHighlightsToDisplay(): TierFeatureHighlight[] {
			if (this.hitEnterpriseTierLimit) {
				return [
					{
						text: "Dynamic instructions per month.",
						icon: "plus",
					},
					{
						text: "Number of custom cores.",
						icon: "plus",
					},
					{
						text: "API invocations per month.",
						icon: "plus",
					},
					{
						text: "Any other Enterprise Tier limit.",
						icon: "playlist-plus",
					},
				];
			}

			if (!this.nextUserTierDetails) {
				// Fallback highlights when tier details aren't loaded
				return [
					{
						text: "Increased computation limits",
						icon: "plus",
					},
					{
						text: "More concurrent tasks",
						icon: "plus",
					},
					{
						text: "Enhanced features and capabilities",
						icon: "playlist-plus",
					},
				];
			}

			switch (this.triggeredLimit) {
				default:
					// Return tier highlights if available, otherwise use fallback
					return this.nextUserTierDetails.shortVersionPoints &&
						this.nextUserTierDetails.shortVersionPoints.length > 0
						? this.nextUserTierDetails.shortVersionPoints
						: [
								{
									text: "Increased computation limits",
									icon: "plus",
								},
								{
									text: "More concurrent tasks",
									icon: "plus",
								},
								{
									text: "Enhanced features and capabilities",
									icon: "playlist-plus",
								},
							];
			}
		},
		hitEnterpriseTierLimit(): boolean {
			const enterpriseHit =
				(this.suggestedUserTierCode === "enterprise-tier-s" &&
					this.currentUserTierCode === "enterprise-tier-s") ||
				(this.suggestedUserTierCode === "enterprise-tier-m" &&
					this.currentUserTierCode === "enterprise-tier-m") ||
				(this.suggestedUserTierCode === "enterprise-tier-l" &&
					this.currentUserTierCode === "enterprise-tier-l");
			return enterpriseHit;
		},
		upgradeButtonStyling() {
			if (this.hitEnterpriseTierLimit) {
				return { text: "Contact Us" };
			} else {
				return { text: "Upgrade" };
			}
		},
	},
	async created() {
		await this.userStore.getCurrentUserTierDetails();
	},
	methods: {
		closeDialog() {
			// @ts-ignore
			this.$posthog?.capture("tier_upgrade_dismissed", {
				current_tier: this.userStore?.currentUserSubscriptions?.Tier,
			});
			this.$emit("update:showDialog", false);
		},
		async initiateUpgrade() {
			this.closeDialog();

			// @ts-ignore
			this.$posthog?.capture("tier_upgrade_button_clicked", {
				planClicked: this.suggestedUserTierCode,
			});

			if (this.quickCheckout) {
				const userCognitoGroups = await this.userStore.getCurrentUserGroups();
				switch (this.suggestedUserTierCode) {
					case "developer-tier":
					case "pro-tier":
						if (userCognitoGroups?.includes("CustomerPortalUser")) {
							this.$emit("customer-portal");
						} else {
							const response = await getStripeCheckoutPublishableKey();
							this.stripePublishableKey = response.data["StripePublicKey"];

							// @ts-ignore FIXME:
							const stripePriceId = this.monthlySubscription
								? tierFromCode(this.suggestedUserTierCode)?.monthlySubscriptionStripePriceId
								: tierFromCode(this.suggestedUserTierCode)?.yearlySubscriptionStripePriceId;
							await this.postCheckoutSession(stripePriceId);
							const { loadStripe } = await import("@stripe/stripe-js");
							const stripe = await loadStripe(this.stripePublishableKey, { locale: "en-GB" });
							if (stripe && this.stripeSessionId) {
								await stripe.redirectToCheckout({ sessionId: this.stripeSessionId });
							}
						}
						break;

					case "enterprise-tier-s":
						this.initiateContactUs("enterprise-tier-s");
						break;
					case "enterprise-tier-m":
						this.initiateContactUs("enterprise-tier-m");
						break;
					case "enterprise-tier-l":
						this.initiateContactUs("enterprise-tier-l");
						break;

					default:
						break;
				}
			} else {
				if (this.hitEnterpriseTierLimit) {
					switch (this.suggestedUserTierCode) {
						case "developer-tier":
						case "pro-tier":
							break;

						case "enterprise-tier-s":
							this.initiateContactUs("enterprise-tier-s");
							break;
						case "enterprise-tier-m":
							this.initiateContactUs("enterprise-tier-m");
							break;
						case "enterprise-tier-l":
							this.initiateContactUs("enterprise-tier-l");
							break;

						default:
							break;
					}
				} else {
					this.goToBillingPage();
				}
			}
		},
		async postCheckoutSession(price_id) {
			try {
				const response = await postCheckoutSession(price_id);
				this.stripeSessionId = response.data["SessionID"];
			} catch (error) {
				monitoringCaptureError(error, "Stripe checkout");
				// @ts-ignore
				this.message = error?.message;
			}
		},
		async initiateContactUs(planClicked) {
			try {
				// @ts-ignore
				this.$posthog?.capture("upgrade_subscription_contact_button_clicked", {
					currentUserTier: this.currentUserTierCode,
					planClicked: planClicked,
				});

				const fullname = this.userStore.currentUser?.name ?? "";
				const email = this.userStore.currentUser?.email ?? "";

				// Determine if user is already on the tier they're trying to access (for managing subscription)
				const isManagingCurrentTier = this.currentUserTierCode === planClicked;

				if (isManagingCurrentTier) {
					// User is already on this Enterprise tier - show manage subscription form
					this.manageSubscriptionFormProps.fullname = fullname;
					this.manageSubscriptionFormProps.email = email;

					switch (planClicked) {
						case "enterprise-tier-s":
							this.manageSubscriptionFormProps.currentTier = "Enterprise S";
							break;
						case "enterprise-tier-m":
							this.manageSubscriptionFormProps.currentTier = "Enterprise M";
							break;
						case "enterprise-tier-l":
							this.manageSubscriptionFormProps.currentTier = "Enterprise L";
							break;
						default:
							this.manageSubscriptionFormProps.currentTier = planClicked;
							break;
					}

					this.showManageSubscriptionFormDialog = true;
				} else {
					// User is interested in a new tier - show contact sales form
					this.contactSalesFormProps.fullname = fullname;
					this.contactSalesFormProps.email = email;

					switch (planClicked) {
						case "enterprise-tier-s":
							this.contactSalesFormProps.plan = "Enterprise S";
							break;
						case "enterprise-tier-m":
							this.contactSalesFormProps.plan = "Enterprise M";
							break;
						case "enterprise-tier-l":
							this.contactSalesFormProps.plan = "Enterprise L";
							break;
						default:
							this.contactSalesFormProps.plan = planClicked;
							break;
					}

					this.showContactSalesFormDialog = true;
				}
			} catch (error) {
				monitoringCaptureError(error, "Initiate contact us");
				// Even if setting up the props failed, we can still show the form
				if (this.currentUserTierCode === planClicked) {
					this.showManageSubscriptionFormDialog = true;
				} else {
					this.showContactSalesFormDialog = true;
				}
			}
		},
		goToBillingPage() {
			this.closeDialog();
			this.$router.push({ path: "/billing" });
		},
		listToReadableString(values: string[]): string {
			const oxfordComma = values.length > 2;
			return values.reduce((accumulator, currentValue, idx) => {
				// if last element
				if (idx === values.length - 1) {
					// add an oxford comma if the list is longer than 2 elements
					accumulator += `${oxfordComma ? ", and " : " and "}`;
				} else {
					accumulator += ", ";
				}

				accumulator += currentValue;
				return accumulator;
			});
		},
		handleContactSalesFormSubmitted() {
			this.showContactSalesFormDialog = false;
			this.successMessage =
				"Contact request submitted successfully! Our sales team will reach out to you shortly.";
			this.successSnackbar = true;
		},
		handleManageSubscriptionFormSubmitted() {
			this.showManageSubscriptionFormDialog = false;
			this.successMessage = "Request submitted successfully! Our team will contact you soon.";
			this.successSnackbar = true;
		},
		handleContactSalesFormSubmissionFailed() {
			this.errorMessage = "Submission failed. Please check your internet connection and try again shortly.";
			this.errorSnackbar = true;
		},
		handleManageSubscriptionFormSubmissionFailed() {
			this.errorMessage = "Submission failed. Please check your internet connection and try again shortly.";
			this.errorSnackbar = true;
		},
	},
});
</script>

<style lang="scss" scoped>
/* Mobile: image stacked above the details. Desktop: details left, image right. */
.upgrade-content {
	display: flex;
	flex-direction: column-reverse;
	gap: var(--sgnl-space-16);
}

.tier-upgrade-details-stack {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
	flex: 1;
	min-width: 0;
}

.side-image {
	width: 100%;
	height: 150px;
	object-fit: cover;
	object-position: center;
	border-radius: var(--sgnl-border-radius-6);
}

.upgrade-compare-link {
	cursor: pointer;
}

@media (min-width: 600px) {
	.upgrade-content {
		flex-direction: row;
		gap: var(--sgnl-space-24);
	}

	.side-image {
		width: 250px;
		height: auto;
		flex-shrink: 0;
	}
}
</style>
